import { ref } from 'vue';
import type { 
  Tutorial, 
  TutorialFunction, 
  PecTutorConfig, 
  PecTutorAPI, 
  WaitOptions,
  MessageOptions,
  QuestionOptions,
  ClickOptions,
  DragOptions,
  MediaOptions,
  ChoiceOption
} from '../types';

// Define a specific interface for the component's exposed methods
interface PecTutorComponentInstance {
  say: (text: string, options?: MessageOptions) => Promise<void>;
  ask: (question: string, options?: QuestionOptions) => Promise<string>;
  choice: (title: string, choices: ChoiceOption[]) => Promise<void>;
  click: (selector: string, options?: ClickOptions) => Promise<void>;
  clickAndDrag: (from: string, to: string, options?: DragOptions) => Promise<void>;
  highlight: (selector: string, duration?: number) => Promise<void>;
  showImage: (src: string, options?: MediaOptions) => Promise<void>;
  showVideo: (src: string, options?: MediaOptions) => Promise<void>;
  waitForInput: (selector: string, options?: WaitOptions) => Promise<boolean>;
  show: () => void;
  hide: () => void;
  isVisible: () => boolean;
}

// Global state for tutorials
const tutorials = ref<Tutorial[]>([]);
const currentTutorial = ref<Tutorial | null>(null);
const isRunningTutorial = ref(false);

// Tutorial execution state
const currentStepIndex = ref(0);
const tutorialPaused = ref(false);

/**
 * Tutorial Manager Composable
 * Handles tutorial registration, execution, and state management
 */
export function useTutorialManager() {
  
  const registerTutorial = (name: string, description: string, tutorialFunction: TutorialFunction): string => {
    const tutorialId = `tutorial_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const tutorial: Tutorial = {
      id: tutorialId,
      name,
      description,
      steps: [], // Will be populated during execution
      pageUrl: window.location.pathname,
      isActive: false
    };
    
    // Store the function reference for execution
    (tutorial as Tutorial & { executionFunction: TutorialFunction }).executionFunction = tutorialFunction;
    
    tutorials.value.push(tutorial);
    
    return tutorialId;
  };

  const startTutorial = async (tutorialId: string): Promise<void> => {
    const tutorial = tutorials.value.find(t => t.id === tutorialId);
    if (!tutorial) {
      console.error(`PecTutor: Tutorial with ID "${tutorialId}" not found`);
      return;
    }

    if (isRunningTutorial.value) {
      // If clicking on the same tutorial that's already running, allow resuming
      if (currentTutorial.value && currentTutorial.value.id === tutorialId) {
        return;
      }
      console.warn('PecTutor: Another tutorial is already running');
      return;
    }

    try {
      isRunningTutorial.value = true;
      currentTutorial.value = tutorial;
      tutorial.isActive = true;
      currentStepIndex.value = 0;

      // Execute the tutorial function
      const executionFunction = (tutorial as Tutorial & { executionFunction: TutorialFunction }).executionFunction;
      if (typeof executionFunction === 'function') {
        await executionFunction();
      }

      // Mark tutorial as completed
      tutorial.isActive = false;

    } catch (error) {
      console.error('PecTutor: Error executing tutorial:', error);
      tutorial.isActive = false;
    } finally {
      isRunningTutorial.value = false;
      currentTutorial.value = null;
      currentStepIndex.value = 0;
    }
  };

  const stopTutorial = (): void => {
    if (currentTutorial.value) {
      currentTutorial.value.isActive = false;
    }
    
    isRunningTutorial.value = false;
    currentTutorial.value = null;
    currentStepIndex.value = 0;
    tutorialPaused.value = false;
  };

  const pauseTutorial = (): void => {
    tutorialPaused.value = true;
  };

  const resumeTutorial = (): void => {
    tutorialPaused.value = false;
  };

  const getCurrentStep = (): number => {
    return currentStepIndex.value;
  };

  const nextStep = (): void => {
    currentStepIndex.value++;
  };

  const getTutorialsForPage = (pageUrl?: string): Tutorial[] => {
    const targetUrl = pageUrl || window.location.pathname;
    return tutorials.value.filter(tutorial => tutorial.pageUrl === targetUrl);
  };

  const removeTutorial = (tutorialId: string): boolean => {
    const index = tutorials.value.findIndex(t => t.id === tutorialId);
    if (index !== -1) {
      tutorials.value.splice(index, 1);
      return true;
    }
    return false;
  };

  const clearAllTutorials = (): void => {
    tutorials.value = [];
    stopTutorial();
  };

  const clearTutorialsForPage = (pageUrl?: string): void => {
    const targetUrl = pageUrl || window.location.pathname;
    tutorials.value = tutorials.value.filter(tutorial => tutorial.pageUrl !== targetUrl);
  };

  return {
    // State
    tutorials,
    currentTutorial,
    isRunningTutorial,
    tutorialPaused,
    currentStepIndex,

    // Methods
    registerTutorial,
    startTutorial,
    stopTutorial,
    pauseTutorial,
    resumeTutorial,
    getCurrentStep,
    nextStep,
    getTutorialsForPage,
    removeTutorial,
    clearAllTutorials,
    clearTutorialsForPage
  };
}

/**
 * Creates the main PecTutor API
 */
/* eslint-disable @typescript-eslint/no-unused-vars */
export function createPecTutorAPI(config: PecTutorConfig): PecTutorAPI {
  const tutorialManager = useTutorialManager();
  
  // Store reference to the active PecTutor component instance
  const componentInstance = ref<PecTutorComponentInstance | null>(null);

  const setComponentInstance = (instance: PecTutorComponentInstance | null) => {
    componentInstance.value = instance;
    (api as PecTutorAPI & { isComponentReady?: boolean }).isComponentReady = !!instance;
  };

  // Core API implementation
  const api: PecTutorAPI = {
    // Message functions
    async say(text: string, options: MessageOptions = {}) {
      if (!componentInstance.value) {
        console.warn('PecTutor: Component not initialized');
        return;
      }
      return componentInstance.value.say(text, options);
    },

    async ask(question: string, options: QuestionOptions = {}) {
      if (!componentInstance.value) {
        console.warn('PecTutor: Component not initialized');
        return '';
      }
      return componentInstance.value.ask(question, options);
    },

    async choice(title: string, choices: ChoiceOption[]) {
      if (!componentInstance.value) {
        console.warn('PecTutor: Component not initialized');
        return;
      }
      return componentInstance.value.choice(title, choices);
    },

    // Interactive functions
    async click(selector: string, options: ClickOptions = {}) {
      if (!componentInstance.value) {
        console.warn('PecTutor: Component not initialized');
        return;
      }
      return componentInstance.value.click(selector, options);
    },

    async clickAndDrag(from: string, to: string, options: DragOptions = {}) {
      if (!componentInstance.value) {
        console.warn('PecTutor: Component not initialized');
        return;
      }
      return componentInstance.value.clickAndDrag(from, to, options);
    },

    // Tutorial management
    createTutorial(name: string, tutorialFunction: TutorialFunction) {
      return tutorialManager.registerTutorial(name, '', tutorialFunction);
    },

    async startTutorial(tutorialId: string) {
      return tutorialManager.startTutorial(tutorialId);
    },

    stopTutorial() {
      tutorialManager.stopTutorial();
    },

    clearTutorialsForPage(pageUrl?: string): void {
      return tutorialManager.clearTutorialsForPage(pageUrl);
    },

    // Waiting functions
    async waitForInput(selector: string, options: WaitOptions = {}) {
      if (!componentInstance.value) {
        console.warn('PecTutor: Component not initialized');
        return Promise.resolve(false);
      }
      return componentInstance.value.waitForInput(selector, options);
    },

    async waitForMouseMove(region?: string, options: WaitOptions = {}) {
      return new Promise((resolve) => {
        let targetElement = document.body;
        
        if (region) {
          const element = document.querySelector(region);
          if (element) {
            targetElement = element as HTMLElement;
          }
        }

        const timeout = options.timeout || 30000;
        let timeoutId: number;

        const handleMouseMove = () => {
          clearTimeout(timeoutId);
          targetElement.removeEventListener('mousemove', handleMouseMove);
          resolve();
        };

        targetElement.addEventListener('mousemove', handleMouseMove);

        if (timeout > 0) {
          timeoutId = window.setTimeout(() => {
            targetElement.removeEventListener('mousemove', handleMouseMove);
            if (options.onTimeout) {
              options.onTimeout();
            }
            resolve();
          }, timeout);
        }
      });
    },

    async waitForElement(selector: string, options: WaitOptions = {}) {
      const { timeout = 30000 } = options;
      const startTime = Date.now();
      
      return new Promise<void>((resolve, reject) => {
        const checkElement = () => {
          const element = document.querySelector(selector);
          
          if (element) {
            const style = window.getComputedStyle(element);
            const rect = element.getBoundingClientRect();
            
            if (style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0) {
              resolve();
              return;
            }
          }
          
          if (Date.now() - startTime >= timeout) {
            const errorMessage = `O elemento esperado (${selector}) não apareceu ou não está visível a tempo.`;
            if (componentInstance.value) {
              componentInstance.value.say(errorMessage, { type: 'error' });
            }
            reject(new Error(errorMessage));
            return;
          }
          
          setTimeout(checkElement, 500);
        };
        
        checkElement();
      });
    },

    // Media functions
    async showImage(src: string, options: MediaOptions = {}) {
      if (!componentInstance.value) {
        console.warn('PecTutor: Component not initialized');
        return;
      }
      return componentInstance.value.showImage(src, options);
    },

    async showVideo(src: string, options: MediaOptions = {}) {
      if (!componentInstance.value) {
        console.warn('PecTutor: Component not initialized');
        return;
      }
      return componentInstance.value.showVideo(src, options);
    },

    // Utility functions
    async highlight(selector: string, duration = 3000) {
      if (!componentInstance.value) {
        console.warn('PecTutor: Component not initialized');
        return;
      }
      return componentInstance.value.highlight(selector, duration);
    },

    async scroll(selector: string, options: { behavior?: 'smooth' | 'instant'; block?: 'start' | 'center' | 'end' } = {}) {
      const element = document.querySelector(selector);
      if (!element) {
        console.warn(`PecTutor: Element not found for scrolling: ${selector}`);
        return;
      }

      element.scrollIntoView({
        behavior: (options.behavior || 'smooth') as ScrollBehavior,
        block: (options.block || 'center') as ScrollLogicalPosition
      });

      // Wait for scroll to complete
      return new Promise(resolve => setTimeout(resolve, 1000));
    },

    // State management
    show() {
      if (componentInstance.value) {
        (componentInstance.value as PecTutorComponentInstance).show();
      }
    },

    hide() {
      if (componentInstance.value) {
        (componentInstance.value as PecTutorComponentInstance).hide();
      }
    },

    isVisible() {
      return componentInstance.value ? (componentInstance.value as PecTutorComponentInstance).isVisible() : false;
    },

    getCurrentTutorial() {
      return tutorialManager.currentTutorial.value;
    }
  };

  // Expose method to set component instance
  (api as PecTutorAPI & { _setComponentInstance: (instance: PecTutorComponentInstance | null) => void })._setComponentInstance = setComponentInstance;
  (api as PecTutorAPI & { isComponentReady?: boolean }).isComponentReady = false;

  return api;
}
